import { createApi } from '@reduxjs/toolkit/query/react'
import { API_ENDPOINTS } from 'Constants'
import { DownloadReportRequest } from 'Models/ResponseModels/Health/HealthSalesItem'
import { ILifeSalesItem } from 'Models/ResponseModels/Life'
import { IPolicyItem } from 'Pages/Customer360/types'
import { IMotorSalesBankItem, IMotorSalesReportBank } from 'Models/ResponseModels/motor'
import { IBgrSalesItem } from 'Pages/Sachet/BGR/General/Modals'
import { bobOperationUrlQuery } from 'Services/baseQuery'
import { makeQuery } from 'Services/general'
import { IGroupSalesReport } from 'Models/ResponseModels/Group/sales'

interface IGetReportsAndDashboards {
  filter_applied: string
  filter_value: string
  total_premium: number
  total_policies: number
  insurer_data: {
    insurer_name: string
    premium: number
  }[]
  lob_data?: {
    insurance_name: string
    premium: number
  }[]
  region_data: {
    name: string
    premium: number
  }[]
  zone_data: {
    name: string
    premium: number
  }[]
  branch_data: {
    name: string
    premium: number
  }[]
  monthly_data: {
    month: string
    premium: number
    month_year: string
  }[]
}

export const bobOperationApi = createApi({
  reducerPath: 'bobOperationUrlQuery',
  baseQuery: bobOperationUrlQuery,
  endpoints: (builder) => ({
    getSalesReport: builder.query<
      {
        data: ILifeSalesItem[]
        message: string
        num_pages: number
        page: number
        status: number
      },
      Record<string, string>
    >({
      query: (filters) => ({
        url: `${API_ENDPOINTS.BOB.GET_SALES_REPORT}`,
        method: 'GET',
        params: filters,
        rawResponse: true,
      }),
    }),
    getBGrSalesReport: builder.query<
      {
        data: IBgrSalesItem[]
        message: string
        num_pages: number
        page: number
        status: number
      },
      Record<string, string>
    >({
      query: (filters) => ({
        url: `${API_ENDPOINTS.BOB.GET_BGR_SALES_REPORT}`,
        method: 'GET',
        params: filters,
        rawResponse: true,
      }),
    }),
    getFireSalesReport: builder.query<
      {
        data: IBgrSalesItem[]
        message: string
        num_pages: number
        page: number
        status: number
      },
      Record<string, string>
    >({
      query: (filters) => ({
        url: `${API_ENDPOINTS.BOB.GET_FIRE_SALES_REPORT}`,
        method: 'GET',
        params: filters,
        rawResponse: true,
      }),
    }),
    getPolicyPdf: builder.query<any, Record<string, string>>({
      query: () => ({
        url: `${API_ENDPOINTS.BOB.GET_SALES_REPORT}`,
        method: 'GET',
        params: {
          type: 'pdf',
        },
        responseType: 'arraybuffer',
        headers: {
          'content-type': 'application/pdf',
        },
      }),
    }),
    getMotorSales: builder.query<
      {
        data: IMotorSalesBankItem[]
        message: string
        num_pages: number
        page: number
        status: number
      },
      Record<string, string>
    >({
      query: (filters) => ({
        url: `${API_ENDPOINTS.BOB.GET_MOTOR_SALES}`,
        method: 'GET',
        params: filters,
        rawResponse: true,
      }),
    }),
    getHealthSalesReport: builder.query<
      {
        data: ILifeSalesItem[]
        message: string
        num_pages: number
        page: number
        status: number
      },
      Record<string, string>
    >({
      query: (filters) => ({
        url: `${API_ENDPOINTS.HEALTH_SALES_REPORT}`,
        method: 'GET',
        params: filters,
        rawResponse: true,
      }),
    }),
    downloadReports: builder.mutation<
      any,
      {
        insurance_type?: string
        date_from?: string
        date_to?: string
        insurance_list?: string[]
      }
    >({
      query: (filters) => ({
        url: `${API_ENDPOINTS.BOB.EXPORT_CSV}`,
        method: 'POST',
        body: filters,
      }),
    }),
    downloadReportsPolling: builder.query<
      any,
      {
        task_id: string
      }
    >({
      query: (filters) => ({
        url: `${API_ENDPOINTS.BOB.EXPORT_CSV}${filters.task_id}/`,
        method: 'GET',
      }),
    }),
    downloadHealthSalesReport: builder.query<string, DownloadReportRequest>({
      query: (filters) => ({
        url: API_ENDPOINTS.HEALTH_SALES_REPORT_DOWNLOAD,
        method: 'GET',
        params: makeQuery(filters),
        responseType: 'arraybuffer',
      }),
    }),
    getGroupHealthSalesReport: builder.query<
      {
        data: IGroupSalesReport[]
        message: string
        num_pages: number
        page: number
        status: number
      },
      Record<string, string>
    >({
      query: (filters) => ({
        url: `${API_ENDPOINTS.GROUP_HEALTH_SALES_REPORT}`,
        method: 'GET',
        params: filters,
        rawResponse: true,
      }),
    }),
    getReportsAndDashboards: builder.query<
      IGetReportsAndDashboards,
      {
        product_type: string
        from_date: string
        to_date: string
        zone?: string
        region_name?: string
        branch?: string
        insurance_list?: string[]
      }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.GET_REPORT_AND_DASHBOARD,
        method: 'GET',
        params: params,
      }),
      transformResponse: (response: { result: IGetReportsAndDashboards[] }) => {
        if (response.result.length === 0) {
          return {
            filter_applied: '',
            filter_value: '',
            total_premium: '' as any,
            total_policies: '' as any,
            insurer_data: [],
            region_data: [],
            monthly_data: [],
          } as any
        }
        return response.result[0]
      },
    }),
    downlaodReportsAndDashboards: builder.query<
      any,
      {
        product_type: string
        from_date: string
        to_date: string
        zone?: string
        region_name?: string
        branch?: string
        is_download?: boolean
      }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.GET_REPORT_AND_DASHBOARD,
        method: 'GET',
        params: params,
        responseType: 'arraybuffer',
      }),
    }),
    getBranchDashboard: builder.query<
      {
        result: {
          branch_sol_id: string
          branch_name: string
          total_target: number
          total_premium_achieved: number
          total_leads: number
          open_leads: number
          closed_leads: number
          converted_leads: number
        }[]
        total_count: number
        page: number
        count: number
      },
      {
        insurance_type: string
        page: number
        zone?: string
        region?: string
        Branch_sol_id?: string
        from_date?: string
        to_date?: string
      }
    >({
      query: (body) => ({
        url: API_ENDPOINTS.GET_BRANCH_DASHBOARD,
        method: 'POST',
        body: body,
      }),
    }),
  }),
})

export const {
  useLazyGetSalesReportQuery,
  useLazyGetPolicyPdfQuery,
  useLazyGetMotorSalesQuery,
  useLazyGetBGrSalesReportQuery,
  useLazyGetFireSalesReportQuery,
  useLazyGetHealthSalesReportQuery,
  useLazyDownloadHealthSalesReportQuery,
  useLazyGetGroupHealthSalesReportQuery,
  useLazyGetReportsAndDashboardsQuery,
  useLazyGetBranchDashboardQuery,
  useGetBranchDashboardQuery,
  useLazyDownlaodReportsAndDashboardsQuery,
  useDownloadReportsMutation,
  useDownloadReportsPollingQuery,
  useLazyDownloadReportsPollingQuery,
} = bobOperationApi
